import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserCheck, Clock, TrendingUp, FileCheck, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const iconMap = {
  UserCheck: UserCheck,
  Clock: Clock,
  TrendingUp: TrendingUp,
  FileCheck: FileCheck
};

const Services = () => {
  const { t } = useTranslation();

  const servicesData = [
    {
      id: 1,
      icon: 'UserCheck',
      title: t('services.service1Title'),
      description: t('services.service1Desc'),
      features: [
        t('services.service1Feature1'),
        t('services.service1Feature2'),
        t('services.service1Feature3'),
        t('services.service1Feature4'),
        t('services.service1Feature5')
      ]
    },
    {
      id: 2,
      icon: 'Clock',
      title: t('services.service2Title'),
      description: t('services.service2Desc'),
      features: [
        t('services.service2Feature1'),
        t('services.service2Feature2'),
        t('services.service2Feature3'),
        t('services.service2Feature4'),
        t('services.service2Feature5')
      ]
    },
    {
      id: 3,
      icon: 'TrendingUp',
      title: t('services.service3Title'),
      description: t('services.service3Desc'),
      features: [
        t('services.service3Feature1'),
        t('services.service3Feature2'),
        t('services.service3Feature3'),
        t('services.service3Feature4'),
        t('services.service3Feature5')
      ]
    },
    {
      id: 4,
      icon: 'FileCheck',
      title: t('services.service4Title'),
      description: t('services.service4Desc'),
      features: [
        t('services.service4Feature1'),
        t('services.service4Feature2'),
        t('services.service4Feature3'),
        t('services.service4Feature4'),
        t('services.service4Feature5')
      ]
    }
  ];
  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden">
      {/* Static TAG Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/82lbuhec_hf_20260205_182144_d08bbcde-2120-4034-9a56-c0e02342a572.png)',
            opacity: 0.5
          }}
        />
      </div>
      
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-cyan-50 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('services.title')} <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">{t('services.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
          {servicesData.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div 
                key={service.id}
                className="group p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl hover:border-cyan-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-cyan-500/20"
              >
                {/* Icon & Title */}
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 group-hover:text-cyan-600 transition-colors mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-700 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-3 pl-2">
                  {service.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className="flex items-start space-x-3 group/item"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform" />
                      <span className="text-slate-700 text-sm leading-relaxed group-hover/item:text-cyan-600 transition-colors">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Background Gradient on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 to-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl -z-10" />
              </div>
            );
          })}
        </div>

        {/* Process Overview */}
        <div className="max-w-5xl mx-auto mb-16">
          {/* White Opaque Oval around heading */}
          <div className="flex justify-center mb-12">
            <div className="bg-white px-12 py-4 rounded-full shadow-md">
              <h3 className="text-2xl font-bold text-slate-900">{t('services.howWeWorkTitle')}</h3>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                title: t('services.step1Title'),
                description: t('services.step1Desc')
              },
              {
                step: '2',
                title: t('services.step2Title'),
                description: t('services.step2Desc')
              },
              {
                step: '3',
                title: t('services.step3Title'),
                description: t('services.step3Desc')
              }
            ].map((step, index) => (
              <div 
                key={index}
                className="relative p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-cyan-600 transition-all duration-300 group shadow-sm"
              >
                {/* Step Number Circle */}
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-xl font-bold text-slate-900">{step.step}</span>
                </div>
                
                <h4 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors">
                  {step.title}
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {step.description}
                </p>

                {/* Connector Arrow */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <div className="w-6 h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-8 lg:p-12 bg-gradient-to-br from-white to-gray-50 border border-cyan-200 rounded-2xl backdrop-blur-sm">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
              {t('services.readyTitle')}
            </h3>
            <p className="text-lg text-slate-700 mb-8 leading-relaxed">
              {t('services.readySubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="group bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
              >
                {t('services.hireCTA')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={scrollToContact}
                size="lg"
                variant="outline"
                className="border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-900/30 hover:border-cyan-600 font-bold px-8 py-6 text-lg rounded-xl backdrop-blur-sm transition-all duration-300"
              >
                {t('services.learnCTA')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
